import React, { useEffect, useRef, useState } from 'react';
import { SelectValue } from 'antd/lib/select';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DropResult,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import './index.less';

import {
  AgentSettingsInput,
  GuardrailSettingsState,
  MaskingDemaskingMode,
  MaskingMode,
  MaskingSettingsInput,
  MaskingTokenMetaMode,
} from '../../../../../gamma-api';
import IbButton from '../../../../components/common/IbButton';
import IbConfirmModal from '../../../../components/common/IbConfirmModal';
import IbCheckbox from '../../../../components/common/IbCheckbox';
import IbIcon from '../../../../components/common/IbIcon';
import IbModal, { IbModalPosition } from '../../../../components/common/IbModal';
import IbScrollSpy from '../../../../components/common/IbScrollSpy';
import IbSelect from '../../../../components/common/IbSelect';
import IbSwitch from '../../../../components/common/IbSwitch';
import IbTypography from '../../../../components/common/IbTypography';
import PromptEditorExpandable from '../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../components/common/PromptEditor/const';
import {
  BLOCK_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  CONTENT_CLASS_NAME,
  FOOTER_CLASS_NAME,
  MAIN_CLASS_NAME as SETTINGS_CLASS_NAME,
} from '../../../../components/Settings/const';
import { DEFAULT_AGENT_SETTINGS } from '../../../../components/Settings/AgentGeneral';
import {
  buildAgentSettingsWithGuardrails,
  createDetectorStep,
  DEFAULT_MASKING_ENTITY_TYPES,
  GuardrailDetectorSettings,
  IMaskingDetectorStep,
  isMaskingEnabled,
  MaskingDetectorTypeValue,
  MaskingEntityType,
  normalizeDetectorForOrder,
  normalizeGuardrailsSettings,
  normalizeMaskingSettings,
} from '../shared/utils';
import { reorderItems } from '../../../../utils/arrayUtil';
import { getTranslateFunc } from '../../../../utils/stringUtil';

import DetectorListItem from './DetectorListItem';
import DetectorModal from './DetectorModal';

const MAIN_CLASS_NAME = 'agent-masking-tab';
const DETECTOR_LIST_CLASS_NAME = `${MAIN_CLASS_NAME}__detector-list`;
const DETECTOR_EMPTY_CLASS_NAME = `${MAIN_CLASS_NAME}__detector-empty`;
const ADDITIONAL_MODAL_CLASS_NAME = `${MAIN_CLASS_NAME}__additional-modal`;
const DETECT_ALL_CLASS_NAME = `${MAIN_CLASS_NAME}__detect-all`;
const ENTITY_TYPE_LIST_CLASS_NAME = `${MAIN_CLASS_NAME}__entity-type-list`;
const ENTITY_TYPE_LIST_ITEM_CLASS_NAME = `${ENTITY_TYPE_LIST_CLASS_NAME}__item`;
const DETECTOR_DROPPABLE_ID = 'masking-detectors';

export interface IMaskingTabProps {
  agentSettings?: AgentSettingsInput;
  onSave: (settings: AgentSettingsInput) => Promise<void>;
}

const getAdditionalDetectorSettings = (masking: MaskingSettingsInput): GuardrailDetectorSettings[] =>
  ((masking.detectors ?? []) as GuardrailDetectorSettings[]).filter(
    (detector) =>
      detector.type === MaskingDetectorTypeValue.Regex || detector.type === MaskingDetectorTypeValue.External
  );

const MaskingTab: React.FC<IMaskingTabProps> = ({ agentSettings, onSave }) => {
  const { t } = useTranslation();
  const maskingTranslate = getTranslateFunc('Agent: Guardrails: Masking:');
  const detectorKeyCounterRef = useRef(0);

  const [masking, setMasking] = useState<MaskingSettingsInput>(() =>
    normalizeMaskingSettings(agentSettings?.guardrails?.masking)
  );
  const [detectorSteps, setDetectorSteps] = useState<IMaskingDetectorStep[]>([]);
  const [saving, setSaving] = useState(false);
  const [creationVisible, setCreationVisible] = useState(false);
  const [additionalSettingsVisible, setAdditionalSettingsVisible] = useState(false);
  const [editingStepKey, setEditingStepKey] = useState<string>();
  const [deletingStepKey, setDeletingStepKey] = useState<string>();

  const getNextDetectorKey = () => {
    detectorKeyCounterRef.current += 1;
    return `masking-detector-${detectorKeyCounterRef.current}`;
  };

  useEffect(() => {
    detectorKeyCounterRef.current = 0;

    const nextMasking = normalizeMaskingSettings(agentSettings?.guardrails?.masking);
    setMasking(nextMasking);
    setDetectorSteps(
      getAdditionalDetectorSettings(nextMasking).map((detector) => createDetectorStep(getNextDetectorKey(), detector))
    );
  }, [agentSettings]);

  const editingStep = detectorSteps.find((step) => step.key === editingStepKey);

  const updateMasking = (patch: Partial<MaskingSettingsInput>) => {
    setMasking((prev) => normalizeMaskingSettings({ ...prev, ...patch }));
  };

  const onMaskingEnabledChange = (checked: boolean) => {
    updateMasking({ state: checked ? GuardrailSettingsState.Enabled : GuardrailSettingsState.Disabled });
  };

  const onDetectAllSupportedEntityTypesChange = (checked: boolean) => {
    updateMasking({ detect_all_supported_entity_types: checked });
  };

  const onBuiltinEntityTypeChange = (type: MaskingEntityType, checked: boolean) => {
    const entities = masking.builtin_entities ?? [];
    updateMasking({
      builtin_entities: checked ? [...entities, type] : entities.filter((entity) => entity !== type),
    });
  };

  const onDemaskingModeChange = (checked: boolean) => {
    updateMasking({
      demasking_mode: checked ? MaskingDemaskingMode.WithDemasking : MaskingDemaskingMode.OnlyMasking,
    });
  };

  const onOpenAdditionalSettings = () => {
    setAdditionalSettingsVisible(true);
  };

  const onCloseAdditionalSettings = () => {
    setAdditionalSettingsVisible(false);
  };

  const onModeChange = (value: SelectValue) => {
    updateMasking({ mode: value as MaskingMode });
  };

  const onTokenMetaChange = (checked: boolean) => {
    updateMasking({
      token_meta: checked ? MaskingTokenMetaMode.Enabled : MaskingTokenMetaMode.Disabled,
    });
  };

  const onCustomPromptChange = (value: string) => {
    updateMasking({ custom_prompt: value });
  };

  const onDetectorStateChange = (stepKey: string, enabled: boolean) => {
    setDetectorSteps((prev) =>
      prev.map((step) =>
        step.key === stepKey
          ? {
              ...step,
              detector: {
                ...step.detector,
                state: enabled ? GuardrailSettingsState.Enabled : GuardrailSettingsState.Disabled,
              } as GuardrailDetectorSettings,
            }
          : step
      )
    );
  };

  const onDetectorMove = (stepKey: string, direction: -1 | 1) => {
    const stepIndex = detectorSteps.findIndex((step) => step.key === stepKey);
    const nextIndex = stepIndex + direction;
    const nextSteps = reorderItems(detectorSteps, stepIndex, nextIndex);

    if (nextSteps !== detectorSteps) {
      setDetectorSteps(nextSteps);
    }
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (
      !destination ||
      source.droppableId !== DETECTOR_DROPPABLE_ID ||
      destination.droppableId !== DETECTOR_DROPPABLE_ID
    ) {
      return;
    }

    if (source.index === destination.index || destination.index >= detectorSteps.length) {
      return;
    }

    const nextSteps = reorderItems(detectorSteps, source.index, destination.index);
    if (nextSteps !== detectorSteps) {
      setDetectorSteps(nextSteps);
    }
  };

  const onOpenCreationModal = () => {
    setCreationVisible(true);
  };

  const onCloseCreationModal = () => {
    setCreationVisible(false);
  };

  const onCreationSave = async (step: IMaskingDetectorStep) => {
    setDetectorSteps((prev) => [...prev, { ...step, key: getNextDetectorKey() }]);
    setCreationVisible(false);
  };

  const onCloseEditingModal = () => {
    setEditingStepKey(undefined);
  };

  const onEditingSave = async (step: IMaskingDetectorStep) => {
    setDetectorSteps((prev) => prev.map((item) => (item.key === editingStepKey ? { ...step, key: item.key } : item)));
    setEditingStepKey(undefined);
  };

  const onDeleteConfirm = () => {
    if (!deletingStepKey) {
      return;
    }

    setDetectorSteps((prev) => prev.filter((step) => step.key !== deletingStepKey));
    setDeletingStepKey(undefined);
  };

  const onDeleteCancel = () => {
    setDeletingStepKey(undefined);
  };

  const onSaveButtonClick = async () => {
    const settings = agentSettings || DEFAULT_AGENT_SETTINGS;
    const guardrails = normalizeGuardrailsSettings(settings.guardrails);
    const nextMasking = normalizeMaskingSettings({
      ...masking,
      detectors: detectorSteps.map((step, index) =>
        normalizeDetectorForOrder(step.detector, index)
      ) as GuardrailDetectorSettings[],
    });
    const nextGuardrails = {
      ...guardrails,
      masking: nextMasking,
    };

    setSaving(true);

    try {
      await onSave(buildAgentSettingsWithGuardrails(settings, nextGuardrails));
    } finally {
      setSaving(false);
    }
  };

  const renderEntityType = (type: MaskingEntityType) => {
    const checked = !!masking.builtin_entities?.includes(type);

    const onEntityTypeChange = (value: boolean) => {
      onBuiltinEntityTypeChange(type, value);
    };

    return (
      <label key={type} className={ENTITY_TYPE_LIST_ITEM_CLASS_NAME}>
        <IbCheckbox value={checked} onChange={onEntityTypeChange} />
        <IbTypography.Paragraph type="secondary">{maskingTranslate(`Entity types: ${type}`)}</IbTypography.Paragraph>
      </label>
    );
  };

  const renderGeneralBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{maskingTranslate('General: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={isMaskingEnabled(masking)} size="small" onChange={onMaskingEnabledChange} />
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: Step: Enabled: Title')}
          </IbTypography.Paragraph>
        </IbTypography>
      </div>
      {isMaskingEnabled(masking) && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {maskingTranslate('Entity types: Title')}
            </IbTypography.Paragraph>
            <div className={DETECT_ALL_CLASS_NAME}>
              <IbTypography>
                <IbSwitch
                  checked={!!masking.detect_all_supported_entity_types}
                  size="small"
                  onChange={onDetectAllSupportedEntityTypesChange}
                />
                <IbTypography.Paragraph strong type="secondary">
                  {maskingTranslate('Detect all supported entity types: Title')}
                </IbTypography.Paragraph>
              </IbTypography>
            </div>
            {!masking.detect_all_supported_entity_types && (
              <IbTypography.Paragraph type="secondary">
                {maskingTranslate('Entity types: Description')}
              </IbTypography.Paragraph>
            )}
            {!masking.detect_all_supported_entity_types && (
              <div className={ENTITY_TYPE_LIST_CLASS_NAME}>{DEFAULT_MASKING_ENTITY_TYPES.map(renderEntityType)}</div>
            )}
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography>
              <IbSwitch
                checked={masking.demasking_mode === MaskingDemaskingMode.WithDemasking}
                size="small"
                onChange={onDemaskingModeChange}
              />
              <IbTypography.Paragraph strong type="secondary">
                {maskingTranslate('Deanonymize responses: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
            <IbTypography.Paragraph type="secondary">
              {maskingTranslate('Deanonymize responses: Description')}
            </IbTypography.Paragraph>
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbButton icon={<IbIcon iconName="setting-two" />} type="secondary" onClick={onOpenAdditionalSettings}>
              {maskingTranslate('General: Advanced settings')}
            </IbButton>
          </div>
        </>
      )}
    </div>
  );

  const renderDetectorItem = (step: IMaskingDetectorStep, index: number) => {
    const renderDraggableDetector = (
      draggableProvided: DraggableProvided,
      draggableSnapshot: DraggableStateSnapshot
    ) => {
      return (
        <DetectorListItem
          draggableProps={draggableProvided.draggableProps}
          dragHandleProps={draggableProvided.dragHandleProps}
          index={index}
          innerRef={draggableProvided.innerRef}
          isDragging={draggableSnapshot.isDragging}
          saving={saving}
          step={step}
          total={detectorSteps.length}
          onDelete={setDeletingStepKey}
          onMove={onDetectorMove}
          onSetup={setEditingStepKey}
          onStateChange={onDetectorStateChange}
        />
      );
    };

    return (
      <Draggable key={step.key} draggableId={step.key} index={index} isDragDisabled={saving}>
        {renderDraggableDetector}
      </Draggable>
    );
  };

  const renderDetectorDroppable = (droppableProvided: DroppableProvided) => (
    <div ref={droppableProvided.innerRef} className={DETECTOR_LIST_CLASS_NAME} {...droppableProvided.droppableProps}>
      {detectorSteps.map(renderDetectorItem)}
      {droppableProvided.placeholder}
    </div>
  );

  const renderDetectorsBlock = () => {
    return (
      isMaskingEnabled(masking) && (
        <div className={BLOCK_CLASS_NAME}>
          <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
            <h4>{maskingTranslate('Detectors: Title')}</h4>
            <IbButton
              disabled={saving}
              icon={<IbIcon iconName="add-one" />}
              type="primary"
              onClick={onOpenCreationModal}
            >
              {maskingTranslate('Detector: Add')}
            </IbButton>
          </IbTypography>
          {!detectorSteps.length ? (
            <div className={DETECTOR_EMPTY_CLASS_NAME}>
              <IbTypography.Paragraph disabled type="secondary">
                {maskingTranslate('Detectors: Empty')}
              </IbTypography.Paragraph>
            </div>
          ) : (
            <Droppable droppableId={DETECTOR_DROPPABLE_ID} isDropDisabled={saving}>
              {renderDetectorDroppable}
            </Droppable>
          )}
        </div>
      )
    );
  };

  const renderAdditionalSettingsModal = () => (
    <IbModal
      className={ADDITIONAL_MODAL_CLASS_NAME}
      footer={
        <IbButton type="secondary" onClick={onCloseAdditionalSettings}>
          {t('Close')}
        </IbButton>
      }
      position={IbModalPosition.Centered}
      title={maskingTranslate('General: Advanced settings')}
      visible={additionalSettingsVisible}
      width={760}
      onCancel={onCloseAdditionalSettings}
    >
      <div className={BLOCK_CLASS_NAME}>
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {maskingTranslate('Mode: Title')}
          </IbTypography.Paragraph>
          <IbSelect
            options={[
              { value: MaskingMode.ReplaceWithToken, label: maskingTranslate('Mode: ReplaceWithToken') },
              { value: MaskingMode.ReplaceWithFake, label: maskingTranslate('Mode: ReplaceWithFake') },
            ]}
            value={masking.mode}
            onChange={onModeChange}
          />
        </div>
        {masking.mode === MaskingMode.ReplaceWithToken && (
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography>
              <IbSwitch
                checked={masking.token_meta === MaskingTokenMetaMode.Enabled}
                size="small"
                onChange={onTokenMetaChange}
              />
              <IbTypography.Paragraph strong type="secondary">
                {maskingTranslate('Token metadata: Title')}
              </IbTypography.Paragraph>
            </IbTypography>
            <IbTypography.Paragraph type="secondary">
              {maskingTranslate('Token metadata: Description')}
            </IbTypography.Paragraph>
          </div>
        )}
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: Builtin: CustomPrompt: Title')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">
            {t('Agent: Guardrails: Builtin: CustomPrompt: Description')}
          </IbTypography.Paragraph>
          <PromptEditorExpandable
            promptLanguage={PromptLanguage.MASKING}
            value={masking.custom_prompt}
            onChange={onCustomPromptChange}
          />
        </div>
      </div>
    </IbModal>
  );

  const sections = [
    { id: 'masking-general', title: maskingTranslate('General: Title'), content: renderGeneralBlock() },
    {
      id: 'masking-detectors',
      title: maskingTranslate('Detectors: Title'),
      content: renderDetectorsBlock(),
    },
  ];

  return (
    <div className={`${SETTINGS_CLASS_NAME} ${MAIN_CLASS_NAME}`}>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className={CONTENT_CLASS_NAME}>
          <IbScrollSpy displayNav={false} sections={sections} />
        </div>
      </DragDropContext>
      <div className={FOOTER_CLASS_NAME}>
        <IbButton disabled={saving} onClick={onSaveButtonClick}>
          {t('Settings: Save')}
        </IbButton>
      </div>
      <DetectorModal isCreation visible={creationVisible} onClose={onCloseCreationModal} onSave={onCreationSave} />
      <DetectorModal step={editingStep} visible={!!editingStep} onClose={onCloseEditingModal} onSave={onEditingSave} />
      {renderAdditionalSettingsModal()}
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onDeleteConfirm}>
              {t('Agent: Guardrails: Steps: Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onDeleteCancel}>
              {t('Cancel')}
            </IbButton>
          </>
        }
        loading={saving}
        title={t('Agent: Guardrails: Steps: DeleteConfirm: Title')}
        visible={!!deletingStepKey}
        onCancel={onDeleteCancel}
      >
        <IbTypography.Paragraph type="secondary">{maskingTranslate('Detector: DeleteConfirm')}</IbTypography.Paragraph>
      </IbConfirmModal>
    </div>
  );
};

export default MaskingTab;
